// 외치기(text) 텍스트 효과 SSOT — 등장(enter)/지속(during)/퇴장(exit) 3슬롯.
//  저장: clip.style.textFx = { enter, during, exit } (cmds argSchema2.style — 가산·하위호환)
//  레거시: textFx 없으면 anim(true→pop 등장, false→없음)으로 해석 → 기존 리액션 무회귀.
//  재생 엔진(playTextFx)은 anime.js v4 (animate/stagger/splitText/createSpring).
import { animate, stagger, createSpring } from "animejs";

export const TEXT_FX_ENTER = [
  { key: "pop", label: "팝 (기본)" },
  { key: "fade", label: "페이드" },
  { key: "typing", label: "타이핑" },
  { key: "scramble", label: "스크램블" },
  { key: "none", label: "없음" },
];
export const TEXT_FX_DURING = [
  { key: "none", label: "없음 (기본)" },
  { key: "bounce", label: "통통" },
  { key: "shake", label: "떨림" },
  { key: "pulse", label: "맥동" },
];
export const TEXT_FX_EXIT = [
  { key: "none", label: "없음 (기본)" },
  { key: "fade", label: "페이드" },
  { key: "pop-out", label: "팝아웃" },
];

// style → 유효한 textFx 해석 (레거시 anim 매핑 포함)
export function resolveTextFx(style) {
  const st = style || {};
  if (st.textFx) {
    return { enter: st.textFx.enter || "pop", during: st.textFx.during || "none", exit: st.textFx.exit || "none" };
  }
  return { enter: st.anim === false ? "none" : "pop", during: "none", exit: "none" };
}

const TYPE_CHAR_MS = 45; // 타이핑 글자당 간격
const SCRAMBLE_CHAR_MS = 30; // 스크램블 글자당 확정 간격
const SCRAMBLE_POOL = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!?#$%&*+=";
const EXIT_MS = { fade: 350, "pop-out": 300, none: 0 };

// el(텍스트 채워진 엘리먼트)에 fx를 재생. totalMs = 클립 길이(퇴장 스케줄 기준).
//  charEls = 글자 span 배열(textSegments.buildCharSpans 산출) — 타이핑/스크램블의 stagger 대상.
//  (anime splitText는 innerHTML을 재구성해 글자별 색 span을 부수므로 자체 span을 쓴다)
//  반환: { cancel } — 언마운트 시 애니/타이머 정리용.
export function playTextFx(el, fx, totalMs, charEls) {
  const anims = [];
  const timers = [];
  let enterMs = 0;

  // ── 등장 ──
  if (fx.enter === "pop") {
    el.style.opacity = "0";
    anims.push(animate(el, { opacity: [0, 1], scale: [0.4, 1], ease: createSpring({ stiffness: 220, damping: 14 }) }));
    enterMs = 450;
  } else if (fx.enter === "fade") {
    el.style.opacity = "0";
    anims.push(animate(el, { opacity: [0, 1], duration: 400, ease: "outQuad" }));
    enterMs = 400;
  } else if (fx.enter === "typing") {
    const chars = charEls || [];
    chars.forEach((c) => (c.style.opacity = "0"));
    anims.push(animate(chars, { opacity: [0, 1], duration: 30, delay: stagger(TYPE_CHAR_MS) }));
    enterMs = chars.length * TYPE_CHAR_MS + 60;
  } else if (fx.enter === "scramble") {
    const chars = charEls || [];
    const finals = chars.map((c) => c.textContent);
    const settleAt = chars.map((_, i) => 180 + i * SCRAMBLE_CHAR_MS);
    const t0 = performance.now();
    const iv = setInterval(() => {
      const t = performance.now() - t0;
      let done = true;
      chars.forEach((c, i) => {
        if (t >= settleAt[i]) c.textContent = finals[i];
        else {
          done = false;
          c.textContent = SCRAMBLE_POOL[(Math.random() * SCRAMBLE_POOL.length) | 0];
        }
      });
      if (done) clearInterval(iv);
    }, 50);
    timers.push(iv);
    enterMs = 180 + chars.length * SCRAMBLE_CHAR_MS + 100;
  }

  // ── 지속 (등장 완료 후 루프) ──
  if (fx.during && fx.during !== "none") {
    timers.push(
      setTimeout(() => {
        if (fx.during === "bounce") anims.push(animate(el, { translateY: [0, -8], duration: 280, ease: "inOutQuad", loop: true, alternate: true }));
        else if (fx.during === "shake") anims.push(animate(el, { translateX: [-3, 3], duration: 90, ease: "linear", loop: true, alternate: true }));
        else if (fx.during === "pulse") anims.push(animate(el, { scale: [1, 1.07], duration: 380, ease: "inOutSine", loop: true, alternate: true }));
      }, enterMs)
    );
  }

  // ── 퇴장 (클립 끝 직전) ──
  const exitMs = EXIT_MS[fx.exit] || 0;
  if (exitMs > 0) {
    const exitAt = Math.max(enterMs, totalMs - exitMs - 30);
    timers.push(
      setTimeout(() => {
        if (fx.exit === "fade") anims.push(animate(el, { opacity: 0, duration: exitMs, ease: "outQuad" }));
        else if (fx.exit === "pop-out") anims.push(animate(el, { opacity: [1, 0], scale: [1, 1.8], duration: exitMs, ease: "inBack" }));
      }, exitAt)
    );
  }

  return {
    cancel() {
      timers.forEach(clearTimeout); // clearTimeout은 interval id에도 유효 (동일 풀)
      anims.forEach((a) => {
        try {
          a.cancel();
        } catch (e) {
          /* noop */
        }
      });
    },
  };
}
